import React, { useState, useEffect } from 'react';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';

const AIPromptQuery = ({ placeholderText = 'e.g., Find active markets with high liquidity about sports events...' }) => {
  // Form state
  const [prompt, setPrompt] = useState('');
  const [generatedQuery, setGeneratedQuery] = useState('');
  const [showQueryPreview, setShowQueryPreview] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Query execution state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const { mergeCurrentContext } = useGlobal();
  const { searchApi } = useBuild();

  useEffect(() => {
    mergeCurrentContext({
      queryBeingBuilt: { prompt, generatedQuery },
    });
    return () => mergeCurrentContext({ queryBeingBuilt: undefined });
  }, [prompt, generatedQuery, mergeCurrentContext]);

  const handleGenerateQuery = async () => {
    if (!prompt.trim()) {
      alert('Please enter a prompt');
      return;
    }

    try {
      setIsGenerating(true);
      setErrorMessage(null);
      setGeneratedQuery('');
      setFeedback('');

      const result = await searchApi.generateAiPromptQuery(prompt);

      if (result && result.error) {
        setErrorMessage(result.error);
        setShowQueryPreview(false);
      } else {
        const esQuery = result?.es_query ?? {};
        const llmFeedback = result?.feedback ?? '';
        setGeneratedQuery(JSON.stringify(esQuery, null, 2));
        setFeedback(llmFeedback);
        setShowQueryPreview(true);
      }
      
    } catch (error) {
      console.error('Error generating query from prompt:', error);
      setErrorMessage(error?.message || 'Failed to generate query');
      setIsGenerating(false);
      return;
    }
    setIsGenerating(false);
  };

  const handleExecuteQuery = async () => {
    if (!generatedQuery.trim()) {
      alert('Please generate a query first');
      return;
    }

    try {
      // Validate JSON
      JSON.parse(generatedQuery);
    } catch (error) {
      alert('Invalid JSON query. Please check the syntax.');
      return;
    }

    const payload = {
      query: JSON.parse(generatedQuery),
    };
    
    console.log('AI Prompt Execute Query Payload:', payload);
    
    try {
      setIsExecuting(true);
      const response = await searchApi.generateEsQueryCandidates(payload);
      console.log('API Response:', response);
      // Results are automatically stored in context by the API method
    } catch (error) {
      console.error('Error executing generated query:', error);
      // Error is automatically handled by the API method in context
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        AI Prompt Query
      </Typography>
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 1, minHeight: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Describe what you're looking for in natural language, and AI will generate an Elasticsearch query:
        </Typography>
        
        <TextField
          label="AI Prompt"
          multiline
          rows={6}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholderText}
          variant="outlined"
        />

        {/* Generate Query Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleGenerateQuery}
            disabled={!prompt.trim() || isGenerating}
            sx={{ minWidth: 200 }}
          >
            {isGenerating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Generating...
              </>
            ) : (
              'Generate Query'
            )}
          </Button>
        </Box>

        {errorMessage && (
          <Alert severity="error">{errorMessage}</Alert>
        )}

        {/* Generated Query Preview (always expanded when available) */}
        {showQueryPreview && (
          <Box>
            <TextField
              label="LLM Feedback"
              multiline
              rows={6}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              variant="outlined"
              sx={{ width: '100%', mb: 2 }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Generated Elasticsearch Query"
              multiline
              rows={12}
              value={generatedQuery}
              onChange={(e) => setGeneratedQuery(e.target.value)}
              variant="outlined"
              sx={{ width: '100%' }}
            />
          </Box>
        )}
      </Box>

      {/* Execute Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleExecuteQuery}
          disabled={!generatedQuery.trim() || isExecuting}
          sx={{ minWidth: 200 }}
        >
          {isExecuting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Executing...
            </>
          ) : (
            'Execute Query'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default AIPromptQuery;

